import React, { useEffect } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $createTableNodeWithDimensions,
  TableNode,
  $isTableNode,
} from "@lexical/table";
import {
  $createParagraphNode,
  $createTextNode,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  createCommand,
  ElementNode,
  LexicalCommand,
} from "lexical";

export interface InsertTableCommandPayload {
  columns: string;
  rows: string;
  includeHeaders?: boolean;
}

export const INSERT_TABLE_COMMAND_PAYLOAD: LexicalCommand<InsertTableCommandPayload> =
  createCommand("INSERT_TABLE_COMMAND_PAYLOAD");

export default function TablePlugin(): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNodes([TableNode])) {
      throw new Error("TablePlugin: TableNode not registered on editor");
    }

    const ensureParagraphBefore = (tableNode: TableNode) => {
      let prev = tableNode.getPreviousSibling();
      if (!prev) {
        const paragraph = $createParagraphNode().append($createTextNode(""));
        tableNode.insertBefore(paragraph);
        prev = paragraph;
      }
      return prev;
    };

    const ensureParagraphAfter = (tableNode: TableNode) => {
      let next = tableNode.getNextSibling();
      if (!next) {
        const paragraph = $createParagraphNode().append($createTextNode(""));
        tableNode.insertAfter(paragraph);
        next = paragraph;
      }
      return next;
    };

    const isWholeTableSelected = (selection: any, tableNode: TableNode) => {
      return (
        $isRangeSelection(selection) &&
        selection.anchor.getNode().getTopLevelElementOrThrow() === tableNode &&
        selection.focus.getNode().getTopLevelElementOrThrow() === tableNode &&
        !selection.isCollapsed()
      );
    };

    // Command to insert a table
    const removeInsertCommand = editor.registerCommand(
      INSERT_TABLE_COMMAND_PAYLOAD,
      ({ columns, rows, includeHeaders }) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          return false;
        }

        const tableNode = $createTableNodeWithDimensions(
          Number(rows),
          Number(columns),
          includeHeaders ?? false
        );

        const paragraphBefore = $createParagraphNode().append(
          $createTextNode("")
        );
        const paragraphAfter = $createParagraphNode().append(
          $createTextNode("")
        );

        selection.insertNodes([paragraphBefore, tableNode, paragraphAfter]);

        return true;
      },
      COMMAND_PRIORITY_LOW
    );

    // Arrow Up
    const removeArrowUp = editor.registerCommand(
      "keyArrowUp" as any,
      (event: KeyboardEvent) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return false;

        const tableNode = selection.anchor
          .getNode()
          .getTopLevelElementOrThrow();

        if ($isTableNode(tableNode)) {
          if (isWholeTableSelected(selection, tableNode)) {
            return false; // allow normal deletion
          }
          const prev = ensureParagraphBefore(tableNode);
          if ($isElementNode(prev)) prev.select();
          event.preventDefault();
          return true;
        }
        return false;
      },
      COMMAND_PRIORITY_LOW
    );

    // Arrow Down
    const removeArrowDown = editor.registerCommand(
      "keyArrowDown" as any,
      (event: KeyboardEvent) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return false;

        const tableNode = selection.anchor
          .getNode()
          .getTopLevelElementOrThrow();

        if ($isTableNode(tableNode)) {
          if (isWholeTableSelected(selection, tableNode)) {
            return false;
          }
          const next = ensureParagraphAfter(tableNode);
          if ($isElementNode(next)) next.select();
          event.preventDefault();
          return true;
        }
        return false;
      },
      COMMAND_PRIORITY_LOW
    );

    // Mouse click fix — if click above/below table and no paragraph exists, insert one
    const removeClickHandler = editor.registerCommand(
      "click" as any,
      (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        if (!target) return false;

        editor.update(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) return;

          const tableNode = selection.anchor
            .getNode()
            .getTopLevelElementOrThrow();

          if ($isTableNode(tableNode)) {
            const { top, bottom } = target.getBoundingClientRect();
            const clickY = event.clientY;

            if (clickY < top) {
              const prev = ensureParagraphBefore(tableNode);
              if ($isElementNode(prev)) prev.select();
            } else if (clickY > bottom) {
              const next = ensureParagraphAfter(tableNode);
              if ($isElementNode(next)) next.select();
            }
          }
        });
        return false;
      },
      COMMAND_PRIORITY_LOW
    );

    return () => {
      removeInsertCommand();
      removeArrowUp();
      removeArrowDown();
      removeClickHandler();
    };
  }, [editor]);

  return null;
}
